import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';

const API_URL = '/api/content';

const ProjectCard = ({ project }) => {
  const shortDesc = project.excerpt || (project.description
    ? project.description.replace(/<[^>]*>/g, '').substring(0, 120) + (project.description.length > 120 ? '...' : '')
    : '');

  return (
    <div className="bg-white rounded-lg overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 flex flex-col h-full">
      <div className="h-56 bg-gradient-to-br from-pink-600 to-pink-800 flex items-center justify-center relative">
        <div className="absolute inset-0 bg-black/20"></div>
        {project.image ? (
          <img src={project.image} alt={project.title} className="w-full h-full object-cover" />
        ) : (
          <span className="text-6xl opacity-50">⚙️</span>
        )}
        <div className="absolute bottom-3 right-3 bg-white/90 px-3 py-1 rounded-full">
          <span className="text-sm font-semibold text-pink-800">{project.category}</span>
        </div>
      </div>

      <div className="p-6 flex flex-col flex-grow">
        <h3 className="text-xl font-bold text-gray-900 mb-2">
          {project.title}
        </h3>
        <p className="text-gray-600 mb-4 flex-grow line-clamp-3">
          {shortDesc}
        </p>

        <div className="grid grid-cols-2 gap-3 mb-4">
          <div className="bg-gray-50 rounded-lg p-3">
            <p className="text-xs text-gray-500 uppercase">Members</p>
            <p className="text-sm font-semibold text-gray-800">{project.members}</p>
          </div>
          <div className="bg-gray-50 rounded-lg p-3">
            <p className="text-xs text-gray-500 uppercase">Software Used</p>
            <p className="text-sm font-semibold text-pink-600">{project.software}</p>
          </div>
        </div>

        <div className="border-t border-gray-200 pt-4 flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-500">
              <span className="font-medium text-gray-700">Market:</span> {project.market}
            </p>
            <p className="text-sm text-gray-500 mt-1">
              <span className="font-medium text-gray-700">Timeline:</span> {project.timeline}
            </p>
          </div>
          {project.id && (
            <Link
              to={`/projects/${project.id}`}
              className="inline-flex items-center text-pink-600 hover:text-pink-700 font-medium text-sm"
            >
              More Details
              <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </Link>
          )}
        </div>
      </div>
    </div>
  );
};

const Projects = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [projects, setProjects] = useState([]);
  const containerRef = useRef(null);

  useEffect(() => {
    fetch(`${API_URL}/projects`)
      .then(res => res.json())
      .then(data => {
        if (Array.isArray(data) && data.length > 0) {
          // Show only featured projects on the homepage
          const featured = data.filter(p => p.featured);
          setProjects(featured);
        }
      })
      .catch(err => console.error('Error fetching projects:', err));
  }, []);

  useEffect(() => {
    if (projects.length === 0) return;
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % projects.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [projects.length]);

  const getDisplaySlides = () => {
    if (projects.length === 0) return [];
    const displaySlides = [];
    for (let i = 0; i < 3; i++) {
      const index = (currentSlide + i) % projects.length;
      displaySlides.push(projects[index]);
    }
    return displaySlides;
  };

  if (projects.length === 0) return null;

  return (
    <section className="py-20 bg-gray-50">
      <div className="container mx-auto px-4 mb-8">
        <div className="text-center">
          <div className="flex items-center justify-center mb-6">
            <div className="flex-1 h-px bg-gray-300"></div>
            <h2 className="text-4xl font-bold text-gray-900 px-8">
              OUR PROJECTS
            </h2>
            <div className="flex-1 h-px bg-gray-300"></div>
          </div>
          <p className="text-lg text-gray-600 max-w-4xl mx-auto leading-relaxed">
            Proven track record with Japanese automotive and industrial clients. From design to manufacturing, delivering quality on time.
          </p>
        </div>
      </div>

      <div className="py-12">
        <div className="container mx-auto px-4">
          <div className="relative max-w-full mx-auto">
            <div ref={containerRef} className="relative overflow-hidden">
              <div className="flex gap-6 justify-center">
                {getDisplaySlides().map((project, index) => (
                  <div key={`${index}`} className="w-full md:w-1/2 lg:w-[calc(33.333%-16px)] flex-shrink-0 px-2">
                    <ProjectCard project={project} />
                  </div>
                ))}
              </div>

              {/* Dots Navigation */}
              <div className="flex justify-center gap-2 mt-8">
                {projects.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentSlide(index)}
                    className={`h-3 rounded-full transition-all ${
                      currentSlide === index 
                        ? 'bg-pink-600 w-8' 
                        : 'bg-gray-300 hover:bg-gray-400 w-3'
                    }`}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Projects;
